import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, BarChart3, TrendingUp, Users, Clock, 
  CheckCircle2, Play, RefreshCw, AlertTriangle, Target,
  Zap, ChevronDown, ChevronUp, Activity, Star
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { API_URL } from '../../utils/apiConfig';

// Metric Card Component
function MetricCard({ title, value, subtitle, icon: Icon, color = 'purple', trend = null, ...rest }) {
  const colorClasses = {
    purple: 'bg-purple-900/50 text-purple-400',
    blue: 'bg-blue-900/50 text-blue-400',
    emerald: 'bg-emerald-900/50 text-emerald-400',
    amber: 'bg-amber-900/50 text-amber-400',
    indigo: 'bg-indigo-900/50 text-indigo-400',
    cyan: 'bg-cyan-900/50 text-cyan-400'
  };

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-4" {...rest}>
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-10 h-10 rounded-lg ${colorClasses[color]} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
        <span className="text-slate-400 text-sm">{title}</span>
      </div>
      <div className="flex items-baseline gap-2">
        <p className="text-2xl font-bold text-white">{value}</p>
        {trend !== null && (
          <span className={`text-xs ${trend >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {trend >= 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      {subtitle && <p className="text-slate-500 text-xs mt-1">{subtitle}</p>}
    </div>
  );
}

// Funnel Step Component
function FunnelStep({ step, name, reached, total, isLast = false }) {
  const percentage = total > 0 ? Math.round((reached / total) * 100) : 0;
  const barColor = percentage >= 80 ? 'bg-emerald-500' : percentage >= 50 ? 'bg-amber-500' : 'bg-red-500';

  return (
    <div className="flex items-center gap-4">
      <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-sm font-medium text-slate-300">
        {step}
      </div>
      <div className="flex-1">
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm text-slate-300">{name}</span>
          <span className="text-sm text-slate-400">{reached} ({percentage}%)</span>
        </div>
        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
          <div 
            className={`h-full ${barColor} transition-all duration-500`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
      {!isLast && (
        <ChevronDown className="w-4 h-4 text-slate-600" />
      )}
    </div>
  );
}

export default function AdminDemoAnalytics() {
  const navigate = useNavigate();
  const getToken = () => localStorage.getItem('pecash_token');
  
  // State
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [daysFilter, setDaysFilter] = useState(30);
  const [expandedSession, setExpandedSession] = useState(null);

  // Fetch analytics data
  const fetchAnalytics = useCallback(async () => {
    const token = getToken();
    setLoading(true);
    
    try {
      // Fetch summary
      const summaryRes = await fetch(`${API_URL}/api/admin/demo-analytics/summary?days=${daysFilter}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (summaryRes.ok) {
        const summaryData = await summaryRes.json();
        setSummary(summaryData);
      }
      
      // Fetch funnel
      const funnelRes = await fetch(`${API_URL}/api/admin/demo-analytics/funnel?days=${daysFilter}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (funnelRes.ok) {
        const funnelData = await funnelRes.json();
        setFunnel(funnelData);
      }
      
      // Fetch recent sessions
      const sessionsRes = await fetch(`${API_URL}/api/admin/demo-analytics/sessions?limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (sessionsRes.ok) {
        const sessionsData = await sessionsRes.json();
        setSessions(sessionsData.sessions || []);
      }
    } catch (err) {
      console.error('Error fetching demo analytics:', err);
      toast.error('Failed to load demo analytics');
    } finally {
      setLoading(false);
    }
  }, [daysFilter]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    if (mins === 0) return `${secs}s`;
    return `${mins}m ${secs}s`;
  };

  return (
    <div className="min-h-screen bg-slate-900 pb-20">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/dashboard')}
              className="text-slate-400 hover:text-white"
              data-testid="demo-analytics-back-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            <div className="h-6 w-px bg-slate-700" />
            <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Demo Analytics</h1>
              <p className="text-sm text-slate-400">Investor demo engagement metrics</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <select
              value={daysFilter}
              onChange={(e) => setDaysFilter(Number(e.target.value))}
              className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
              data-testid="demo-analytics-period-filter"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <Button
              onClick={fetchAnalytics}
              variant="ghost"
              className="text-slate-400 hover:text-white"
              data-testid="demo-analytics-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      {/* Demo Data Warning Banner */}
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="bg-amber-900/30 border border-amber-700/50 rounded-xl p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
          <div>
            <p className="text-amber-300 font-medium text-sm">DEMO ANALYTICS</p>
            <p className="text-amber-400/80 text-xs">SIMULATED DATA — NOT PRODUCTION TRACTION. This tracks investor demo sessions only.</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16">
          <RefreshCw className="w-8 h-8 text-indigo-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading demo analytics...</p>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-6 space-y-8">
          {/* Summary Metrics */}
          <section data-testid="demo-analytics-summary">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-indigo-400" />
              Session Overview ({daysFilter} days)
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <MetricCard
                title="Total Sessions"
                value={summary?.total_sessions || 0}
                icon={Play}
                color="purple"
                data-testid="demo-total-sessions"
              />
              <MetricCard
                title="Completed"
                value={summary?.completed_sessions || 0}
                icon={CheckCircle2}
                color="emerald"
                data-testid="demo-completed-sessions"
              />
              <MetricCard
                title="Completion Rate"
                value={`${summary?.completion_rate || 0}%`}
                icon={Target}
                color="blue"
                data-testid="demo-completion-rate"
              />
              <MetricCard
                title="Avg Duration"
                value={formatDuration(summary?.avg_duration_seconds)}
                subtitle={summary?.avg_duration_minutes > 0 ? `${summary.avg_duration_minutes} min` : null}
                icon={Clock}
                color="amber"
                data-testid="demo-avg-duration"
              />
              <MetricCard
                title="Avg Steps"
                value={summary?.avg_steps_completed?.toFixed(1) || '0'}
                subtitle="out of 13"
                icon={Zap}
                color="cyan"
                data-testid="demo-avg-steps"
              />
              <MetricCard
                title="Avg Rating"
                value={summary?.feedback?.avg_rating || '-'}
                subtitle={`${summary?.feedback?.total_feedback || 0} reviews`}
                icon={Star}
                color="indigo"
                data-testid="demo-avg-rating"
              />
            </div>
          </section>

          {/* By Demo Mode */}
          {summary?.by_demo_mode && Object.keys(summary.by_demo_mode).length > 0 && (
            <section>
              <h2 className="text-lg font-semibold text-white mb-4">By Demo Mode</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(summary.by_demo_mode).map(([mode, count]) => (
                  <div key={mode} className="bg-slate-800 border border-slate-700 rounded-xl p-4">
                    <p className="text-slate-400 text-sm capitalize">{mode || 'Unknown'}</p>
                    <p className="text-2xl font-bold text-white">{count}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Funnel Analysis */}
          {funnel && funnel.total_started > 0 && (
            <section data-testid="demo-analytics-funnel">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-400" />
                Demo Funnel (Guided Mode)
              </h2>
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
                <div className="mb-4 flex items-center justify-between">
                  <p className="text-slate-400 text-sm">Total Started: <span className="text-white font-medium">{funnel.total_started}</span></p>
                  <p className="text-slate-500 text-xs">Drop-off visualization by step</p>
                </div>
                <div className="space-y-4">
                  {funnel.funnel?.map((step, index) => (
                    <FunnelStep
                      key={step.step}
                      step={step.step}
                      name={step.name}
                      reached={step.sessions_reached}
                      total={funnel.total_started}
                      isLast={index === funnel.funnel.length - 1}
                    />
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Recent Sessions */}
          <section data-testid="demo-analytics-sessions">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-400" />
              Recent Demo Sessions
            </h2>
            
            {sessions.length === 0 ? (
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-8 text-center">
                <BarChart3 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No demo sessions recorded yet</p>
                <p className="text-slate-500 text-sm mt-2">Run the Investor Demo to start tracking</p>
              </div>
            ) : (
              <div className="space-y-3">
                {sessions.map((session) => (
                  <div 
                    key={session.session_id}
                    className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden"
                  >
                    <div 
                      className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-750"
                      onClick={() => setExpandedSession(expandedSession === session.session_id ? null : session.session_id)}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          session.completed ? 'bg-emerald-900/50' : 'bg-slate-700'
                        }`}>
                          {session.completed ? (
                            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                          ) : (
                            <Play className="w-5 h-5 text-slate-400" />
                          )}
                        </div>
                        <div>
                          <p className="text-white font-medium text-sm">{session.session_id}</p>
                          <p className="text-slate-500 text-xs">
                            {new Date(session.started_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="text-slate-400 text-xs">Mode</p>
                          <p className="text-white text-sm capitalize">{session.demo_mode || '-'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-slate-400 text-xs">Steps</p>
                          <p className="text-white text-sm">{session.steps_completed?.length || 0}/13</p>
                        </div>
                        <div className="text-right">
                          <p className="text-slate-400 text-xs">Duration</p>
                          <p className="text-white text-sm">{formatDuration(session.total_duration_seconds)}</p>
                        </div>
                        {session.rating && (
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                            <span className="text-white text-sm">{session.rating}</span>
                          </div>
                        )}
                        {expandedSession === session.session_id ? (
                          <ChevronUp className="w-5 h-5 text-slate-500" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-500" />
                        )}
                      </div>
                    </div>
                    
                    {/* Expanded Details */}
                    {expandedSession === session.session_id && (
                      <div className="border-t border-slate-700 p-4 bg-slate-850">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-slate-500">Started</p>
                            <p className="text-slate-300">{new Date(session.started_at).toLocaleString()}</p>
                          </div>
                          {session.completed_at && (
                            <div>
                              <p className="text-slate-500">Completed</p>
                              <p className="text-slate-300">{new Date(session.completed_at).toLocaleString()}</p>
                            </div>
                          )}
                          <div>
                            <p className="text-slate-500">Personas Viewed</p>
                            <p className="text-slate-300">
                              {session.personas_viewed?.length > 0 
                                ? session.personas_viewed.join(', ') 
                                : '-'}
                            </p>
                          </div>
                          <div>
                            <p className="text-slate-500">Steps Completed</p>
                            <p className="text-slate-300">
                              {session.steps_completed?.length > 0 
                                ? session.steps_completed.sort((a,b) => a-b).join(', ') 
                                : 'None'}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Feedback Summary */}
          {summary?.feedback && summary.feedback.total_feedback > 0 && (
            <section>
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Star className="w-5 h-5 text-amber-400" />
                Feedback Summary
              </h2>
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
                <div className="grid grid-cols-3 gap-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-white">{summary.feedback.avg_rating}</p>
                    <p className="text-slate-400 text-sm">Average Rating</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-white">{summary.feedback.total_feedback}</p>
                    <p className="text-slate-400 text-sm">Total Reviews</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-white">{summary.feedback.follow_ups}</p>
                    <p className="text-slate-400 text-sm">Follow-ups Requested</p>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Empty State */}
          {!summary || summary.total_sessions === 0 && (
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-12 text-center">
              <BarChart3 className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-white text-lg font-semibold mb-2">No Demo Data Yet</h3>
              <p className="text-slate-400 mb-6">Run the Investor Demo to start tracking engagement metrics</p>
              <Button
                onClick={() => navigate('/admin/investor-demo')}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="demo-analytics-start-demo-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                Start Investor Demo
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
